<template>
  <div ref="container" class="custom-popover">
    <div
      ref="item"
      :id="idCalc"
      aria-describedby="tooltip"
      class="item tw-cursor-pointer"
      :class="innerClass"
      :data-test="`${testId ? `${testId}-` : ''}popover-open-button`"
    >
      <slot />
    </div>
    <div
      ref="content"
      :id="`${idCalc}-content`"
      role="tooltip"
      class="content"
      @click.prevent
      :class="`${styleClass} ${innerClass}`"
      v-if="header || title || hasMessage || helpLink"
    >
      <div class="arrow" id="arrow" data-popper-arrow />
      <IconButton
        @click="hide"
        :icon="{
          default: { icon: 'fa-regular fa-xmark' },
          hover: { icon: 'fa-solid fa-xmark' },
        }"
        class="tw-px-2 !tw-absolute !tw-py-0 !tw-top-2 !tw-right-2"
        :testId="`${testId ? `${testId}-` : ''}popover-close-button`"
      />
      <div v-if="header" class="content-header">
        <div class="content-header-title">
          {{ header }}
        </div>
      </div>
      <div class="content-body">
        <h6 v-if="title">
          {{ title }}
        </h6>
        <slot v-if="hasMessage" name="message">
          <p v-if="message" v-html="message"></p>
        </slot>
        <template v-for="(help, h) in helpLink" :key="h">
          <p class="text-end">
            <Btn
              variant="link"
              href=""
              @click="helpAction(help)"
              :testId="`${testId ? `${testId}-` : ''}popover-help-link`"
            >
              <span v-if="!help.icon">{{ help.name || "Learn More" }}</span>
              <NavLinkIcon
                v-if="help.icon"
                :item="help"
                class="tw-ml-1"
                :testId="`${testId ? `${testId}-` : ''}-help-icon-link`"
              />
            </Btn>
          </p>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import Btn from "@/components/display/Btn";
import { createPopper } from "@popperjs/core";
import IconButton from "@/components/display/IconButton.vue";
import NavLinkIcon from "@/components/display/NavLinkIcon";
import uniqueId from "lodash-es/uniqueId";
import { useClickOutside } from "@/utils";

export default {
  name: "Popover",
  components: {
    Btn,
    IconButton,
    NavLinkIcon,
  },
  props: {
    header: {
      type: String,
      default: null,
    },
    title: {
      type: String,
      default: null,
    },
    message: {
      type: String,
      default: null,
    },
    helpLink: Array,
    styleClass: {
      type: String,
      default: "tw-bg-beta",
    },
    position: {
      type: String,
      default: "right",
    },
    id: String,
    innerClass: {
      type: String,
      default: "",
    },
    testId: String,
  },
  setup(props, { slots }) {
    const POSITIONS = {
      left: "left",
      right: "right",
      top: "top",
      bottom: "bottom",
    };

    const container = ref(null);
    const content = ref(null);
    const item = ref(null);
    const listener = ref(null);

    const hasMessage = computed(() => !!slots.message || props.message);
    const position = computed(() => POSITIONS[props.position]);

    const helpAction = (help) => {
      if (help.action) {
        help.action();
      }
    };

    const idCalc = computed(() => props.id || uniqueId("popover-"));

    const hide = () => content.value.removeAttribute("data-show");

    onMounted(() => {
      listener.value = useClickOutside(container.value, hide);
      window.addEventListener("click", listener.value);

      const popperInstance = createPopper(item.value, content.value, {
        placement: position.value,
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [0, 8],
            },
          },
        ],
      });

      const show = (event) => {
        event.preventDefault();
        if (content.value.hasAttribute("data-show")) {
          hide();
        } else {
          content.value.setAttribute("data-show", "");
          popperInstance.update();
        }
      };

      const showEvents = ["click", "focus"];

      showEvents.forEach((event) => {
        item.value.addEventListener(event, show);
      });
    });

    onBeforeUnmount(() => {
      window.removeEventListener("click", listener.value);
    });

    return {
      container,
      content,
      hasMessage,
      helpAction,
      hide,
      idCalc,
      item,
    };
  },
};
</script>

<style lang="scss" scoped>
.custom-popover {
  @apply tw-flex;

  .item {
    @apply tw-inline-block;
  }

  .content {
    @apply tw-bg-beta tw-text-alpha tw-border-nu;
    display: none;
    text-align: left;
    max-width: 25rem;
    width: auto;
    padding: 0.125rem;
    position: absolute;
    font-size: 14px;
    font-weight: normal;
    z-index: 200000;

    &[data-show] {
      display: block;
    }
  }

  .arrow {
    display: none !important;
  }

  .content-header {
    height: 2rem;
    padding: 0 0.75rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;

    .content-header-title {
      font-size: 1rem;
      // font-weight: bold;
      font-weight: 500;
      line-height: 1rem;
    }
  }

  .content-body {
    padding: 0.5rem 0.75rem 0 0.75rem;
  }
  p.text-end {
    margin-bottom: 0.5rem !important;
  }
}
</style>
